import type { ContactFormStep } from './constants';
import type { User } from './types';
import type {
	ContactFormModalProps,
	ContactMethod
} from '~/components/ContactFormModal';

import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';

import { CONTACT_FORM_STEPS, STEP_NAMES } from './constants';

interface ContactFormEventsContext {
	props: ContactFormModalProps;
	currentStep: Ref<ContactFormStep>;
	currentPageName: ComputedRef<string>;
	errorMessage: Ref<string | null>;
	getAccountStatus: ComputedRef<string>;
	name: ComputedRef<string>;
	phone: ComputedRef<string>;
	email: ComputedRef<string>;
	userMessage: ComputedRef<string>;
	user: ComputedRef<User | null>;
}

const RETURN_STEP_MAPPING: Partial<Record<ContactFormStep, ContactFormStep>> = {
	[CONTACT_FORM_STEPS.OTP_FORM]: CONTACT_FORM_STEPS.CONTACT_FORM,
	[CONTACT_FORM_STEPS.ERROR_MESSAGE]: CONTACT_FORM_STEPS.CONTACT_FORM
};

export function useContactFormEvents(context: ContactFormEventsContext) {
	const {
		props,
		currentStep,
		currentPageName,
		errorMessage,
		getAccountStatus,
		name,
		phone,
		email,
		userMessage,
		user
	} = context;

	const {
		trackFormPropertyViewed,
		trackFormPropertyError,
		trackFormPropertyAuthentication,
		trackFormPropertyCompleted,
		trackingGoToPrevious,
		trackingCloseClicked,
		trackSignAndLogin,
		trackLinkClicked
	} = useEvents();

	function addEventReturnClicked() {
		const previousStep = RETURN_STEP_MAPPING[currentStep.value];

		if (!previousStep) {
			return;
		}

		trackingGoToPrevious({
			[MixpanelProps.CURRENT_PAGE]: currentPageName.value as
				| 'Detalhe do Imóvel'
				| 'Detalhe do Condomínio',
			[MixpanelProps.CURRENT_FLOW]: 'lead form',
			[MixpanelProps.CURRENT_STEP]: STEP_NAMES[previousStep]
		});
	}

	function trackError() {
		trackFormPropertyError({
			[MixpanelProps.CONDO_NAME]: props.condoName || null,
			[MixpanelProps.COMMERCIAL_ID]: props.propertyCommercialId || null,
			[MixpanelProps.ERROR_TYPE]: errorMessage.value || ''
		});
	}

	function trackCloseModal() {
		trackingCloseClicked({
			[MixpanelProps.CURRENT_PAGE]: currentPageName.value as
				| 'Detalhe do Imóvel'
				| 'Detalhe do Condomínio',
			[MixpanelProps.CURRENT_FLOW]: 'lead form',
			[MixpanelProps.CURRENT_STEP]: STEP_NAMES[currentStep.value]
		});
	}

	function trackOtpForm() {
		trackFormPropertyAuthentication({
			[MixpanelProps.CONDO_NAME]: props.condoName || null,
			[MixpanelProps.COMMERCIAL_ID]: props.propertyCommercialId || null,
			[MixpanelProps.ASKING_PRICE]: props.askingPrice || null,
			[MixpanelProps.COMPANY_NAME]: props.companyName || null,
			[MixpanelProps.AGENT_NAME]: props.broker?.name || null,
			[MixpanelProps.INPUT_NAME]: name.value,
			[MixpanelProps.INPUT_PHONE]: phone.value,
			[MixpanelProps.INPUT_EMAIL]: email.value || '',
			[MixpanelProps.INPUT_MESSAGE]: userMessage.value || ''
		});
	}

	function trackSuccess() {
		trackFormPropertyCompleted({
			[MixpanelProps.CONDO_NAME]: props.condoName || null,
			[MixpanelProps.COMMERCIAL_ID]: props.propertyCommercialId || null,
			[MixpanelProps.ASKING_PRICE]: props.askingPrice || null,
			[MixpanelProps.COMPANY_NAME]: props.companyName || null,
			[MixpanelProps.AGENT_NAME]: props.broker?.name || null,
			[MixpanelProps.INPUT_NAME]: name.value,
			[MixpanelProps.INPUT_PHONE]: phone.value,
			[MixpanelProps.INPUT_EMAIL]: email.value || '',
			[MixpanelProps.INPUT_MESSAGE]: userMessage.value || '',
			[MixpanelProps.ACCOUNT_STATUS]: getAccountStatus.value as
				| 'logged out'
				| 'logged in'
		});
	}

	function trackFormOpened() {
		trackFormPropertyViewed({
			[MixpanelProps.CONDO_NAME]: props.condoName || null,
			[MixpanelProps.COMMERCIAL_ID]: props.propertyCommercialId || null,
			[MixpanelProps.COMPANY_NAME]: props.companyName || null,
			[MixpanelProps.AGENT_NAME]: props.broker?.name || null,
			[MixpanelProps.ASKING_PRICE]: props.askingPrice || null,
			[MixpanelProps.USER_STATUS]: getAccountStatus.value as
				| 'logged out'
				| 'logged in',
			[MixpanelProps.CURRENT_PAGE]: currentPageName.value as
				| 'Detalhe do Imóvel'
				| 'Detalhe do Condomínio'
		});
	}

	function trackTermsOrPolicy(type: 'terms' | 'policy') {
		trackSignAndLogin.trackingTermsOrpolicy({
			[MixpanelProps.USER_NAME]: user.value?.name ?? '',
			[MixpanelProps.USER_EMAIL]: user.value?.email ?? '',
			[MixpanelProps.USER_PHONE]: user.value?.phoneNumber ?? '',
			[MixpanelProps.CLICK_FORM]: true,
			[MixpanelProps.TERMS_OF_USE]: type === 'terms',
			[MixpanelProps.PRIVACY_POLICY]: type === 'policy'
		});
	}

	function trackContactMethodClick(link: ContactMethod) {
		trackLinkClicked({
			[MixpanelProps.CURRENT_PAGE]: currentPageName.value,
			[MixpanelProps.SECTION_NAME]: 'form property error',
			[MixpanelProps.DESTINATION_URL]: link.route,
			[MixpanelProps.LINK_TEXT]: link.label
		});
	}

	const stepEventsMap: Partial<Record<ContactFormStep, () => void>> = {
		[CONTACT_FORM_STEPS.OTP_FORM]: trackOtpForm,
		[CONTACT_FORM_STEPS.SUCCESS_MESSAGE]: trackSuccess,
		[CONTACT_FORM_STEPS.ERROR_MESSAGE]: trackError
	};

	watch(currentStep, () => {
		const eventHandler = stepEventsMap[currentStep.value];
		if (eventHandler) {
			eventHandler();
		}
	});

	return {
		addEventReturnClicked,
		trackError,
		trackCloseModal,
		trackOtpForm,
		trackSuccess,
		trackFormOpened,
		trackTermsOrPolicy,
		trackContactMethodClick,
		// Aliases for compatibility
		addEventsCloseModal: trackCloseModal,
		addEventSuccessMessage: trackSuccess,
		addEventError: trackError,
		addEventContactFormOpened: trackFormOpened,
		addEventsTermsOrPolicy: trackTermsOrPolicy,
		handleContactMethodClick: trackContactMethodClick
	};
}
